import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import AutoSuggestInput from "@/components/AutoSuggestInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  Upload,
  Download,
  Printer,
  Settings,
  Percent,
  Trash2,
  FileSpreadsheet,
  FileText,
  Presentation,
  RefreshCw,
  Search,
  Edit3,
  Flame,
  RotateCcw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  EyeOff,
  ArrowUpAZ,
  ArrowDownAZ,
  ArrowUp01,
  ArrowDown10,
  Check,
  ChevronsUpDown,
  Plus,
  History,
  Tag,
  AlertTriangle,
  AlertCircle,
  Package,
  Calendar,
  IndianRupee,
  FileWarning,
  Clock,
  Loader2
} from "lucide-react";
import PrintPreview from "@/components/PrintPreview";
import { BrandedPriceHistory, BrandedProductsList } from "@/components/BrandedPriceHistory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format price to 2 decimal places
const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(2);
};

// Columns that should have search boxes
const SEARCHABLE_COLUMNS = ["product_name", "generic_name", "company", "category", "second_name"];

// Columns that should be hidden by default
const DEFAULT_HIDDEN_COLUMNS = ["second_name", "third_name", "fourth_name", "fifth_name", "sixth_name", "last_update", "image_link", "narration", "suggested_usd_price"];

// Pagination constant
const ITEMS_PER_PAGE = 50;

// Convert a FastAPI/Pydantic error response into a human-readable toast line.
// Detail can be:
//   • a string (our custom HTTPException)
//   • an array of {loc, msg, type} objects (Pydantic validation errors)
//   • anything else → fall back to the provided label.
const formatApiError = (errorData, fallback = "Update failed") => {
  if (!errorData) return fallback;
  const d = errorData.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    return d
      .slice(0, 3)
      .map((e) => {
        const field = Array.isArray(e.loc)
          ? e.loc.filter((x) => x !== "body").join(".")
          : "";
        return field ? `${field}: ${e.msg}` : e.msg;
      })
      .filter(Boolean)
      .join(" · ") || fallback;
  }
  if (d && typeof d === "object") {
    try { return JSON.stringify(d).slice(0, 180); } catch { return fallback; }
  }
  return fallback;
};

const ALL_COLUMNS = [
  { key: "serial_no", label: "S.No", width: "55px", sortable: false },
  { key: "is_hidden", label: "Hidden", width: "70px", sortable: true, sortType: "boolean" },
  { key: "product_name", label: "Product Name", width: "180px", sortable: true, sortType: "text" },
  { key: "generic_name", label: "Generic Name", width: "140px", sortable: true, sortType: "text" },
  { key: "company", label: "Company", width: "130px", sortable: true, sortType: "text" },
  { key: "box_packaging", label: "Box Packaging", width: "95px", sortable: true, sortType: "text" },
  { key: "weight_per_strip", label: "Weight", width: "85px", sortable: true, sortType: "number" },
  { key: "unit", label: "Unit", width: "70px", sortable: true, sortType: "text" },
  { key: "category", label: "Category", width: "110px", sortable: true, sortType: "text" },
  { key: "price", label: "Price", width: "85px", sortable: true, sortType: "number" },
  { key: "branded_mrp", label: "Branded MRP", width: "95px", sortable: true, sortType: "number" },
  { key: "suggested_usd_price", label: "Suggested $ Price", width: "105px", sortable: true, sortType: "number" },
  { key: "second_name", label: "2nd Name", width: "130px", sortable: true, sortType: "text" },
  { key: "third_name", label: "3rd Name", width: "130px", sortable: true, sortType: "text", hidden: true },
  { key: "fourth_name", label: "4th Name", width: "130px", sortable: true, sortType: "text", hidden: true },
  { key: "fifth_name", label: "5th Name (Alias)", width: "130px", sortable: true, sortType: "text", hidden: true },
  { key: "sixth_name", label: "6th Name (Alias)", width: "130px", sortable: true, sortType: "text", hidden: true },
  { key: "last_update", label: "Last Update", width: "95px", sortable: true, sortType: "text" },
  { key: "image_link", label: "Image Link", width: "100px", sortable: false },
  { key: "narration", label: "Narration", width: "130px", sortable: true, sortType: "text" }
];

// Columns visible to error_manager (limited access - only name editing)
const ERROR_MANAGER_COLUMNS = ["serial_no", "product_name", "second_name", "third_name", "fourth_name", "fifth_name", "sixth_name"];

const ProductsPage = () => {
  usePageTitle("Products");
  const { token, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [canSeeHidden, setCanSeeHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadTime, setLoadTime] = useState(null); // Track API response time in ms
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Check if user is error_manager (limited access)
  const isErrorManager = user?.role === 'error_manager';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Initialize visible columns excluding default hidden ones
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.map(c => c.key).filter(k => !DEFAULT_HIDDEN_COLUMNS.includes(k))
  );
  const [showHiddenColumns, setShowHiddenColumns] = useState(false);
  
  // Get effective columns based on role (error_manager sees limited columns)
  const effectiveColumns = useMemo(() => {
    if (isErrorManager) {
      return ALL_COLUMNS.filter(col => ERROR_MANAGER_COLUMNS.includes(col.key));
    }
    return ALL_COLUMNS.filter(col => visibleColumns.includes(col.key));
  }, [isErrorManager, visibleColumns]);
  const [columnSearches, setColumnSearches] = useState({});
  const [serverSearchTerm, setServerSearchTerm] = useState(""); // For server-side search
  const [serverSearchColumn, setServerSearchColumn] = useState(""); // Which column drives the server search
  // Multi-column sorting: array of { key, direction } objects
  const [sortConfigs, setSortConfigs] = useState([]);
  const [priceAdjustment, setPriceAdjustment] = useState("");
  const [adjustingPrices, setAdjustingPrices] = useState(false);
  const [printAllProducts, setPrintAllProducts] = useState(null); // full dataset for Print/Export, null when not loaded
  const [printAllLoading, setPrintAllLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showUploadModeDialog, setShowUploadModeDialog] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [dryRunPreview, setDryRunPreview] = useState(null); // {header_row, parsed_rows, to_insert, will_delete, sample_products, mapped_columns}
  const [uploadMode, setUploadMode] = useState("append"); // "wipe" or "append"
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({});
  const [printColumns, setPrintColumns] = useState(["product_name", "generic_name", "company", "price", "branded_mrp"]);
  const [printFilter, setPrintFilter] = useState({ type: "", value: [] });
  const [filterValueOpen, setFilterValueOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("product_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const fileInputRef = useRef(null);
  const priceHistoryFileInputRef = useRef(null);
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [deletingItems, setDeletingItems] = useState(false);
  
  // Branded Products / Price History
  const [activeTab, setActiveTab] = useState("all"); // "all" or "branded"
  const [showPriceHistory, setShowPriceHistory] = useState(null); // Product to show history for
  const [showPriceHistoryUpload, setShowPriceHistoryUpload] = useState(false); // Price history upload dialog
  const [priceHistoryUploading, setPriceHistoryUploading] = useState(false);
  
  // Add New Product Dialog
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    product_name: "",
    generic_name: "",
    company: "",
    box_packaging: "",
    weight_per_strip: "",
    unit: "",
    category: "",
    price: "",
    branded_mrp: "",
    suggested_usd_price: "",
    second_name: "",
    third_name: "",
    fourth_name: "",
    fifth_name: "",
    sixth_name: "",
    narration: "",
    image_link: ""
  });
  const [addingProduct, setAddingProduct] = useState(false);

  // Price History Upload Errors Panel
  const [priceHistoryErrors, setPriceHistoryErrors] = useState([]);
  const [showPriceHistoryErrors, setShowPriceHistoryErrors] = useState(false);
  const [priceHistoryUploadStats, setPriceHistoryUploadStats] = useState(null);

  // Edit Name Modal (for error_manager)
  const [showEditNameDialog, setShowEditNameDialog] = useState(false);
  const [editNameProduct, setEditNameProduct] = useState(null);
  const [savingName, setSavingName] = useState(false);

  const fetchProducts = useCallback(async (page = currentPage, searchQuery = serverSearchTerm, searchColumn = serverSearchColumn) => {
    try {
      setLoading(true);
      const startTime = performance.now(); // Start timing
      
      // Build query params for server-side pagination
      const params = new URLSearchParams({
        page: page.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add search parameter if provided. Category/company searches use their
      // dedicated precise server filters; everything else uses the broad
      // multi-field `search` (names, generic, company, category).
      if (searchQuery && searchQuery.trim().length >= 2) {
        if (searchColumn === 'category') {
          params.set('category', searchQuery.trim());
        } else if (searchColumn === 'company') {
          params.set('company', searchQuery.trim());
        } else {
          params.set('search', searchQuery.trim());
        }
      }
      
      const response = await fetch(`${BACKEND_URL}/api/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const endTime = performance.now(); // End timing
      setLoadTime(Math.round(endTime - startTime)); // Store load time in ms
      const data = await response.json();
      setProducts(data.products || []);
      setCanSeeHidden(data.can_see_hidden || false);
      
      // Update pagination state
      if (data.pagination) {
        setTotalCount(data.pagination.total_count || 0);
        setTotalPages(data.pagination.total_pages || 1);
      } else {
        setTotalCount(data.total || 0);
        setTotalPages(Math.ceil((data.total || 0) / ITEMS_PER_PAGE));
      }
    } catch (error) {
      toast.error("Failed to load products");
      setLoadTime(null);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, sortBy, sortOrder, serverSearchTerm, serverSearchColumn]);

  // Toggle product hidden status
  const handleToggleHidden = async (productId, currentStatus) => {
    if (!isAdmin) {
      toast.error("Only admins can hide/show products");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/toggle-hidden`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: [productId], is_hidden: !currentStatus })
      });
      
      if (response.ok) {
        toast.success(currentStatus ? "Product is now visible" : "Product is now hidden");
        fetchProducts();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update");
      }
    } catch (error) {
      toast.error("Failed to update product visibility");
    }
  };

  // Bulk hide/show selected products
  const handleBulkHideShow = async (hide) => {
    if (!isAdmin) {
      toast.error("Only admins can hide/show products");
      return;
    }
    
    if (selectedIds.length === 0) {
      toast.error("Select products first");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/toggle-hidden`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds, is_hidden: hide })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedIds([]);
        fetchProducts();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update");
      }
    } catch (error) {
      toast.error("Failed to update products");
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [currentPage, sortBy, sortOrder]);

  // Page change handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle Add New Product
  const handleAddProduct = async () => {
    if (!newProduct.product_name.trim()) {
      toast.error("Product name is required");
      return;
    }
    
    setAddingProduct(true);
    
    // Use XMLHttpRequest to avoid response body interception issues
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BACKEND_URL}/api/products/add`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    
    xhr.onload = function() {
      setAddingProduct(false);
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || "Unknown error" };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        toast.success("Product added successfully!");
        setShowAddProductDialog(false);
        setNewProduct({
          product_name: "",
          generic_name: "",
          company: "",
          box_packaging: "",
          weight_per_strip: "",
          unit: "",
          category: "",
          price: "",
          branded_mrp: "",
          suggested_usd_price: "",
          second_name: "",
          narration: "",
          image_link: ""
        });
        fetchProducts();
      } else {
        const errorMessage = data.detail || data.message || "Failed to add product";
        toast.error(errorMessage);
      }
    };
    
    xhr.onerror = function() {
      setAddingProduct(false);
      toast.error("Network error - please try again");
    };
    
    xhr.send(JSON.stringify(newProduct));
  };

  // Toggle hidden columns visibility
  const toggleHiddenColumns = () => {
    if (showHiddenColumns) {
      // Hide the default hidden columns
      setVisibleColumns(prev => prev.filter(k => !DEFAULT_HIDDEN_COLUMNS.includes(k)));
    } else {
      // Show all columns
      setVisibleColumns(ALL_COLUMNS.map(c => c.key));
    }
    setShowHiddenColumns(!showHiddenColumns);
  };

  // Filter products based on column searches
  const filteredProducts = useMemo(() => {
    let result = products.filter(product => {
      return Object.entries(columnSearches).every(([key, value]) => {
        if (!value) return true;
        // The server already filtered this column's search across ALL fields
        // (e.g. "testosterone" may match via generic_name) — do NOT re-filter
        // client-side or server matches on other fields get hidden.
        if (key === serverSearchColumn && value === serverSearchTerm) return true;
        const searchValue = value.toLowerCase();
        
        // For product_name search, also check second_name, third_name, fourth_name
        if (key === 'product_name') {
          const productName = String(product.product_name || "").toLowerCase();
          const secondName = String(product.second_name || "").toLowerCase();
          const thirdName = String(product.third_name || "").toLowerCase();
          const fourthName = String(product.fourth_name || "").toLowerCase();
          const fifthName = String(product.fifth_name || "").toLowerCase();
          const sixthName = String(product.sixth_name || "").toLowerCase();
          return productName.includes(searchValue) || 
                 secondName.includes(searchValue) || 
                 thirdName.includes(searchValue) || 
                 fourthName.includes(searchValue) ||
                 fifthName.includes(searchValue) ||
                 sixthName.includes(searchValue);
        }
        
        // For other columns, normal search
        const productValue = String(product[key] || "").toLowerCase();
        return productValue.includes(searchValue);
      });
    });

    // Apply multi-column sorting
    if (sortConfigs.length > 0) {
      result = [...result].sort((a, b) => {
        for (const sortConfig of sortConfigs) {
          const col = ALL_COLUMNS.find(c => c.key === sortConfig.key);
          const aVal = a[sortConfig.key] || "";
          const bVal = b[sortConfig.key] || "";

          let comparison = 0;
          if (col?.sortType === "number") {
            const aNum = parseFloat(aVal) || 0;
            const bNum = parseFloat(bVal) || 0;
            comparison = sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
          } else {
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            if (sortConfig.direction === "asc") {
              comparison = aStr.localeCompare(bStr);
            } else {
              comparison = bStr.localeCompare(aStr);
            }
          }
          
          // If not equal, return the comparison result
          if (comparison !== 0) {
            return comparison;
          }
          // If equal, continue to next sort column
        }
        return 0;
      });
    }

    return result;
  }, [products, columnSearches, sortConfigs, serverSearchColumn, serverSearchTerm]);

  // With server-side pagination, we use products directly (already paginated)
  // Client-side filtering still applies for column searches
  const paginatedProducts = useMemo(() => {
    // If no client-side filters, use products as-is (already paginated from server)
    if (Object.keys(columnSearches).length === 0 || Object.values(columnSearches).every(v => !v)) {
      return products;
    }
    // Apply client-side filtering on already paginated results
    return filteredProducts;
  }, [products, filteredProducts, columnSearches]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [columnSearches, sortConfigs]);

  // Handle multi-column sorting
  const handleSort = (key, direction) => {
    setSortConfigs(prev => {
      // Find if this column is already in sort configs
      const existingIndex = prev.findIndex(s => s.key === key);
      
      if (existingIndex >= 0) {
        // Column already exists in sort
        if (prev[existingIndex].direction === direction) {
          // Same direction clicked - remove this column from sort
          return prev.filter((_, i) => i !== existingIndex);
        } else {
          // Different direction - update the direction
          return prev.map((s, i) => i === existingIndex ? { key, direction } : s);
        }
      } else {
        // Add new column to sort (append to end)
        return [...prev, { key, direction }];
      }
    });
  };
  
  // Get sort order number for a column (1, 2, 3, etc.)
  const getSortOrder = (key) => {
    const index = sortConfigs.findIndex(s => s.key === key);
    return index >= 0 ? index + 1 : null;
  };
  
  // Get sort direction for a column
  const getSortDirection = (key) => {
    const config = sortConfigs.find(s => s.key === key);
    return config?.direction || null;
  };
  
  // Clear all sorts
  const clearAllSorts = () => {
    setSortConfigs([]);
  };

  const handleColumnSearch = (key, value) => {
    setColumnSearches(prev => ({
      ...prev,
      [key]: value
    }));
    
    // All searchable columns are searched server-side (across ALL pages).
    // The backend `search` matches product_name, alt names, generic_name,
    // company and category; category/company get precise dedicated filters.
    if (SEARCHABLE_COLUMNS.includes(key)) {
      setServerSearchTerm(value);
      setServerSearchColumn(key);
      setCurrentPage(1); // Reset to page 1 when searching
    }
  };

  // Debounced server search - triggers fetch when serverSearchTerm changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (serverSearchTerm.length === 0 || serverSearchTerm.length >= 2) {
        fetchProducts(1, serverSearchTerm, serverSearchColumn);
      }
    }, 400); // 400ms debounce
    
    return () => clearTimeout(timer);
  }, [serverSearchTerm, serverSearchColumn]);

  const clearAllSearches = () => {
    setColumnSearches({});
    setServerSearchTerm("");
    setServerSearchColumn("");
    setCurrentPage(1);
    fetchProducts(1, "", "");
  };

  const activeSearchCount = Object.values(columnSearches).filter(v => v).length;

  // Get unique values for autocomplete options.
  // When the Print/Export dialog has loaded the full dataset, use that — otherwise
  // the dropdown only sees generics/companies/categories from the visible 50 rows.
  const autocompleteOptions = useMemo(() => {
    const source = printAllProducts ?? products;
    const genericNames = [...new Set(source.map(p => p.generic_name).filter(Boolean))].sort();
    const companies = [...new Set(source.map(p => p.company).filter(Boolean))].sort();
    const categories = [...new Set(source.map(p => p.category).filter(Boolean))].sort();

    return {
      generic_name: genericNames,
      company: companies,
      category: categories
    };
  }, [products, printAllProducts]);

  // Get filtered products for print preview
  const printPreviewProducts = useMemo(() => {
    // When the Print/Export dialog is open, use the FULL dataset (all pages).
    // Falls back to the visible page if the full fetch hasn't loaded yet.
    const source = printAllProducts ?? products;
    let result = [...source];

    if (printFilter.type === "branded_mrp") {
      result = result.filter(p => p.branded_mrp && p.branded_mrp.toString().trim() !== "");
    } else if (printFilter.type && Array.isArray(printFilter.value) && printFilter.value.length > 0) {
      const selected = new Set(printFilter.value.map(v => v.toLowerCase()));
      result = result.filter(p => {
        const val = (p[printFilter.type] || "").toString().toLowerCase();
        return selected.has(val);
      });
    }

    return result;
  }, [products, printAllProducts, printFilter]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(paginatedProducts.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };

  const handleCellClick = (product, column) => {
    if (column === "serial_no") return;
    setEditingCell({ productId: product.id, column });
    setEditValue(product[column] || "");
  };

  const handleCellSave = async () => {
    if (!editingCell) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${editingCell.productId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [editingCell.column]: editValue })
      });

      if (response.ok) {
        toast.success("Cell updated");
        fetchProducts();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(formatApiError(errorData, "Update failed"));
      }
    } catch (error) {
      toast.error("Update failed");
    }

    setEditingCell(null);
    setEditValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCellSave();
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length === 0) {
      toast.error("Select products to edit");
      return;
    }
    
    if (selectedIds.length === 1) {
      // Single product edit
      const product = products.find(p => p.id === selectedIds[0]);
      if (product) {
        setEditingProduct({ ...product });
        setBulkEditMode(false);
        setShowEditDialog(true);
      }
    } else {
      // Multi-product bulk edit
      setBulkEditMode(true);
      setBulkEditData({});
      setEditingProduct(null);
      setShowEditDialog(true);
    }
  };

  const handleBulkSave = async () => {
    // Filter out empty values
    const updates = Object.fromEntries(
      Object.entries(bulkEditData).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
    );
    
    if (Object.keys(updates).length === 0) {
      toast.error("Please fill at least one field to update");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/products/bulk-update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ids: selectedIds,
          updates
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowEditDialog(false);
        setBulkEditData({});
        setBulkEditMode(false);
        setSelectedIds([]);
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Bulk update failed");
      }
    } catch (error) {
      toast.error("Bulk update failed");
    }
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;

    // Normalize payload:
    //  • Drop metadata that must never be mutated via edit (id, created_at, _id).
    //  • Coerce `is_hidden` to a real boolean (or drop it when empty) to avoid
    //    a Pydantic `bool_parsing` 422 on empty string.
    //  • Drop null / undefined fields so they aren't sent as literal "null"
    //    (JSON serialises undefined away but null would overwrite fields).
    const raw = { ...editingProduct };
    delete raw.id;
    delete raw._id;
    delete raw.created_at;
    delete raw.price_history;
    // eslint-disable-next-line no-unused-vars
    const { is_hidden, ...rest } = raw;
    const payload = {};
    for (const [k, v] of Object.entries(rest)) {
      if (v === null || v === undefined) continue;
      payload[k] = v;
    }
    if (is_hidden !== undefined && is_hidden !== null && is_hidden !== "") {
      if (typeof is_hidden === "string") {
        const v = is_hidden.trim().toLowerCase();
        payload.is_hidden = ["true", "1", "yes", "y"].includes(v);
      } else {
        payload.is_hidden = !!is_hidden;
      }
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${editingProduct.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Product updated successfully");
        setShowEditDialog(false);
        setEditingProduct(null);
        setSelectedIds([]);
        fetchProducts();
      } else {
        // Read error message from response. FastAPI returns `detail` as a
        // string (HTTPException) OR an array of Pydantic validation errors.
        const errorData = await response.json().catch(() => ({}));
        const msg = formatApiError(errorData, "Update failed");
        // eslint-disable-next-line no-console
        console.error("Product PUT failed:", response.status, errorData);
        toast.error(msg);
      }
    } catch (error) {
      toast.error("Update failed: " + (error.message || "Unknown error"));
    }
  };

  // Handle Edit Name for error_manager role
  const handleOpenEditName = (product) => {
    setEditNameProduct({
      id: product.id,
      product_name: product.product_name || "",
      second_name: product.second_name || "",
      third_name: product.third_name || "",
      fourth_name: product.fourth_name || "",
      fifth_name: product.fifth_name || "",
      sixth_name: product.sixth_name || ""
    });
    setShowEditNameDialog(true);
  };

  const handleSaveProductName = async () => {
    if (!editNameProduct) return;
    
    setSavingName(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${editNameProduct.id}/update-name`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_name: editNameProduct.product_name,
          second_name: editNameProduct.second_name,
          third_name: editNameProduct.third_name,
          fourth_name: editNameProduct.fourth_name,
          fifth_name: editNameProduct.fifth_name,
          sixth_name: editNameProduct.sixth_name
        })
      });

      if (response.ok) {
        toast.success("Product name updated successfully");
        setShowEditNameDialog(false);
        setEditNameProduct(null);
        fetchProducts();
      } else {
        const errorData = await response.json().catch(() => ({ detail: "Update failed" }));
        toast.error(errorData.detail || "Failed to update product name");
      }
    } catch (error) {
      toast.error("Failed to update product name: " + (error.message || "Unknown error"));
    } finally {
      setSavingName(false);
    }
  };

  const handlePriceAdjust = async () => {
    const percentage = parseFloat(priceAdjustment);
    if (isNaN(percentage) || percentage === 0) {
      toast.error("Please enter a non-zero percentage (e.g. 10 or -5)");
      return;
    }

    const scopeMsg = selectedIds.length > 0
      ? `${selectedIds.length} selected product(s)`
      : `ALL ${totalCount.toLocaleString()} non-branded products`;
    const direction = percentage > 0 ? "increase" : "decrease";
    if (!window.confirm(
      `Adjust prices by ${direction === "increase" ? "+" : ""}${percentage}%?\n\n` +
      `Scope: ${scopeMsg}\n` +
      `Branded products (with MRP) are always skipped.\n\n` +
      `Original prices are saved — click "Reset Original" to revert.`
    )) return;

    setAdjustingPrices(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/adjust-prices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          percentage,
          ids: selectedIds.length > 0 ? selectedIds : null
        })
      });

      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || data.message || `Adjustment failed (HTTP ${response.status})`);
        return;
      }
      toast.success(data.message || `Adjusted by ${percentage}%`);
      await fetchProducts();
      setPriceAdjustment("");
    } catch (error) {
      toast.error("Price adjustment failed: " + (error.message || "network error"));
    } finally {
      setAdjustingPrices(false);
    }
  };

  const handleResetPrices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/reset-prices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          percentage: 0,
          ids: selectedIds.length > 0 ? selectedIds : null
        })
      });

      const data = await response.json();
      toast.success(data.message);
      fetchProducts();
    } catch (error) {
      toast.error("Reset prices failed");
    }
  };

  // Trigger delete confirmation dialog
  const triggerBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("Select products to delete");
      return;
    }
    setDeleteAction({ type: "bulk", count: selectedIds.length });
    setShowDeleteDialog(true);
  };

  // Execute bulk delete after password confirmation
  const executeBulkDelete = async () => {
    setDeletingItems(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds })
      });

      const data = await response.json();
      toast.success(data.message);
      setSelectedIds([]);
      fetchProducts();
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setDeletingItems(false);
      setDeleteAction(null);
    }
  };

  // Handle file selection - show mode dialog first
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Only Excel files (.xlsx, .xls) are supported");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    
    // Store file and show mode selection dialog
    setPendingUploadFile(file);
    setShowUploadDialog(false);
    setShowUploadModeDialog(true);
  };

  // Perform the actual upload with selected mode
  // Wipe & Replace uses a two-step flow: first a dry-run preview (so the
  // user can eyeball parsed rows / header row / duplicates before the
  // destructive wipe fires), then confirm to commit. Append mode goes
  // straight through.
  const runDryRun = useCallback(async (fileArg, mode = "wipe") => {
    const f = fileArg || pendingUploadFile;
    if (!f) return null;
    setDryRunLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      formData.append("mode", mode);
      formData.append("dry_run", "true");
      const resp = await fetch(`${BACKEND_URL}/api/products/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || "Preview failed");
        return null;
      }
      setDryRunPreview(data);
      return data;
    } catch (e) {
      toast.error("Preview failed");
      return null;
    } finally {
      setDryRunLoading(false);
    }
  }, [pendingUploadFile, token]);

  const downloadDeltaReport = () => {
    const updates = dryRunPreview?.all_updates || [];
    if (!updates.length) {
      toast.error("No price changes to export");
      return;
    }
    const rows = updates.map((u) => {
      const oldP = parseFloat(u.old_price) || 0;
      const newP = parseFloat(u.new_price) || 0;
      const delta = newP - oldP;
      const pct = oldP ? (delta / oldP) * 100 : 0;
      return {
        "Product Name": u.product_name,
        "Generic Name": u.generic_name || "",
        "Company": u.company || "",
        "Category": u.category || "",
        "Old Price": u.old_price,
        "New Price": u.new_price,
        "Δ": delta.toFixed(2),
        "% Change": `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`,
        "Direction": delta > 0 ? "UP" : delta < 0 ? "DOWN" : "SAME",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 40 }, { wch: 24 }, { wch: 18 }, { wch: 18 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Price Delta");
    const src = dryRunPreview.filename || "upload";
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `price_delta_${src.replace(/\.[^.]+$/, "")}_${stamp}.xlsx`);
    toast.success(`Delta report downloaded (${rows.length} price changes)`);
  };

  const saveColumnAliasAndRetry = async (source, target) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/products/column-aliases`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ source, target }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || "Could not save alias");
        return;
      }
      toast.success(`Remembered: "${source}" ⇒ ${target}`);
      // Re-run using the mode the current preview was created with.
      await runDryRun(null, dryRunPreview?.mode || "wipe");
    } catch (e) {
      toast.error("Could not save alias");
    }
  };

  const handleFileUpload = async (mode) => {
    if (!pendingUploadFile) return;

    // Step 1: dry-run preview (both wipe AND append now show a preview first)
    if (!dryRunPreview) {
      await runDryRun(null, mode);
      return;
    }

    // Step 2: real commit — use whatever mode the preview was built for
    const commitMode = dryRunPreview.mode || mode;
    const formData = new FormData();
    formData.append("file", pendingUploadFile);
    formData.append("mode", commitMode);

    try {
      const response = await fetch(`${BACKEND_URL}/api/products/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchProducts();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    }

    setShowUploadModeDialog(false);
    setPendingUploadFile(null);
    setDryRunPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/download-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "product_template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  // Price History Template Download
  const handleDownloadPriceHistoryTemplate = async (includeProducts) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/products/branded-price-history/template?include_products=${includeProducts}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = includeProducts 
        ? "branded_price_history_template_with_products.xlsx" 
        : "branded_price_history_template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(includeProducts 
        ? "Template with products downloaded" 
        : "Blank template downloaded"
      );
    } catch (error) {
      toast.error("Download failed: " + error.message);
    }
  };

  // Price History Upload
  const handlePriceHistoryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setPriceHistoryUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use XMLHttpRequest for more reliable file upload handling
      const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/products/branded-price-history/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (e) {
              reject(new Error('Invalid server response'));
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || 'Upload failed'));
            } catch (e) {
              reject(new Error(`Upload failed: ${xhr.status}`));
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Upload timeout'));
        xhr.timeout = 120000; // 2 minutes timeout
        
        xhr.send(formData);
      });
      
      const data = await uploadPromise;
      
      // Store upload stats
      setPriceHistoryUploadStats({
        added: data.added || 0,
        updated: data.updated || 0,
        skipped: data.skipped || 0
      });
      
      // Show success message
      toast.success(data.message || 'Upload successful');
      
      // Show errors in panel if any
      if (data.errors && data.errors.length > 0) {
        setPriceHistoryErrors(data.errors);
        setShowPriceHistoryErrors(true);
        toast.warning(`${data.errors.length} rows had issues. Click "View Errors" to see details.`);
      } else {
        setPriceHistoryErrors([]);
      }
      
      setShowPriceHistoryUpload(false);
      fetchProducts();
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || 'Upload failed');
    } finally {
      setPriceHistoryUploading(false);
      if (priceHistoryFileInputRef.current) {
        priceHistoryFileInputRef.current.value = '';
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      printColumns.forEach(col => params.append("columns", col));
      if (printFilter.type && Array.isArray(printFilter.value) && printFilter.value.length > 0) {
        params.append("filter_by", printFilter.type);
        printFilter.value.forEach(v => params.append("filter_value", v));
      }

      const response = await fetch(`${BACKEND_URL}/api/products/export-pdf?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("PDF generated");
    } catch (error) {
      toast.error("Export failed");
    }
    setShowPrintDialog(false);
  };

  const handleExportPPTX = async () => {
    try {
      const params = new URLSearchParams();
      printColumns.forEach(col => params.append("columns", col));
      if (printFilter.type && Array.isArray(printFilter.value) && printFilter.value.length > 0) {
        params.append("filter_by", printFilter.type);
        printFilter.value.forEach(v => params.append("filter_value", v));
      }

      const response = await fetch(`${BACKEND_URL}/api/products/export-pptx?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "product_catalog.pptx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Catalog generated");
    } catch (error) {
      toast.error("Export failed");
    }
    setShowPrintDialog(false);
  };

  const hasBrandedMRP = (product) => {
    const mrp = product.branded_mrp;
    if (mrp === null || mrp === undefined || mrp === "") return false;
    const mrpStr = String(mrp).trim();
    return mrpStr !== "" && mrpStr !== "0" && mrpStr !== "nan";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/25">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Product Price List</h1>
            <div className="text-sm text-muted-foreground mt-0.5 flex items-center flex-wrap gap-2">
              {filteredProducts.length} products • {selectedIds.length} selected
              {activeSearchCount > 0 && (
                <span className="text-orange-600">• {activeSearchCount} search filter(s) active</span>
              )}
              {sortConfigs.length > 0 && (
                <span className="text-blue-600">
                  • Sorted by: {sortConfigs.map((s, i) => {
                    const col = ALL_COLUMNS.find(c => c.key === s.key);
                    return `${i + 1}. ${col?.label} (${s.direction === 'asc' ? '↑' : '↓'})`;
                  }).join(', ')}
                </span>
              )}
              {loadTime !== null && !loading && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${loadTime < 200 ? 'border-green-400 text-green-600 bg-green-50' : loadTime < 500 ? 'border-yellow-400 text-yellow-600 bg-yellow-50' : 'border-red-400 text-red-600 bg-red-50'}`}
                  data-testid="load-time-badge"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {loadTime}ms
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          {activeSearchCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllSearches}
              className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
            >
              <X className="w-4 h-4" />
              Clear Searches
            </Button>
          )}
          {sortConfigs.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllSorts}
              className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <X className="w-4 h-4" />
              Clear Sorts ({sortConfigs.length})
            </Button>
          )}
          
          {/* Admin-only buttons - hidden for error_manager */}
          {!isErrorManager && (
            <>
              {/* Add New Product Button */}
              <Button
                size="sm"
                onClick={() => setShowAddProductDialog(true)}
                className="gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              >
                <span className="text-lg leading-none">+</span>
                Add Product
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUploadDialog(true)}
                data-testid="upload-btn"
                className="gap-2 border-orange-300 hover:bg-orange-50 hover:border-orange-400"
              >
                <Upload className="w-4 h-4 text-orange-500" />
                Upload
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                data-testid="download-template-btn"
                className="gap-2 border-emerald-300 hover:bg-emerald-50 hover:border-emerald-400"
              >
                <Download className="w-4 h-4 text-emerald-500" />
                Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPriceHistoryUpload(true)}
                className="gap-2 border-purple-300 hover:bg-purple-50 hover:border-purple-400"
              >
                <History className="w-4 h-4 text-purple-500" />
                Price History
              </Button>
              {/* Show View Errors button if there are errors */}
              {priceHistoryErrors.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPriceHistoryErrors(true)}
                  className="gap-2 border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-700"
                >
                  <AlertTriangle className="w-4 h-4" />
                  View Errors ({priceHistoryErrors.length})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  setShowPrintDialog(true);
                  // Fetch ALL products (not just the current page) so Print/Export
                  // operates on the whole table, not the 50 visible rows.
                  setPrintAllLoading(true);
                  try {
                    const params = new URLSearchParams({
                      page: "1",
                      limit: "100000",
                      sort_by: sortBy,
                      sort_order: sortOrder,
                    });
                    const res = await fetch(`${BACKEND_URL}/api/products?${params}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const data = await res.json();
                    setPrintAllProducts(data.products || []);
                  } catch (e) {
                    toast.error("Failed to load full product list for export");
                    setPrintAllProducts(null);
                  } finally {
                    setPrintAllLoading(false);
                  }
                }}
                data-testid="print-btn"
                className="gap-2 border-blue-300 hover:bg-blue-50 hover:border-blue-400"
              >
            <Printer className="w-4 h-4 text-blue-500" />
            Print/Export
          </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchProducts}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </>
          )}
          
          {/* Refresh button - available for all users */}
          {isErrorManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchProducts}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Controls Bar - Hidden for error_manager */}
      {!isErrorManager && (
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gradient-to-r from-orange-50 via-emerald-50 to-blue-50 rounded-lg border border-orange-200">
        {/* Price Adjustment */}
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <Percent className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <Input
            type="number"
            placeholder="Adjust %"
            value={priceAdjustment}
            onChange={(e) => setPriceAdjustment(e.target.value)}
            className="w-20 sm:w-24 h-8 text-sm"
            data-testid="price-adjust-input"
            inputMode="decimal"
          />
          <Button
            size="sm"
            onClick={handlePriceAdjust}
            disabled={adjustingPrices || !priceAdjustment}
            data-testid="apply-price-adjust"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md min-w-[80px]"
          >
            {adjustingPrices ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {adjustingPrices ? "Applying…" : "Apply"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleResetPrices}
            data-testid="reset-prices-btn"
            className="gap-1.5 border-emerald-400 hover:bg-emerald-50 text-emerald-600"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Reset Original</span>
            <span className="sm:hidden">Reset</span>
          </Button>
          {selectedIds.length > 0 ? (
            <span className="text-[11px] text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full whitespace-nowrap">
              Scope: {selectedIds.length} selected
            </span>
          ) : (
            <span className="text-[11px] text-slate-500 whitespace-nowrap hidden sm:inline">
              Scope: all non-branded
            </span>
          )}
        </div>

        {/* Column Visibility */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="column-settings">
              <Settings className="w-4 h-4" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Visible Columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {ALL_COLUMNS.map(col => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={visibleColumns.includes(col.key)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setVisibleColumns(prev => [...prev, col.key]);
                  } else {
                    setVisibleColumns(prev => prev.filter(k => k !== col.key));
                  }
                }}
              >
                {col.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Show/Hide Hidden Columns Toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={toggleHiddenColumns}
          data-testid="toggle-hidden-columns"
          className={`gap-2 ${showHiddenColumns ? 'border-purple-400 bg-purple-50 text-purple-700' : 'border-slate-300'}`}
        >
          {showHiddenColumns ? (
            <>
              <EyeOff className="w-4 h-4" />
              Hide Extra Columns
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              Show Hidden Columns
            </>
          )}
        </Button>

        {/* Edit & Delete Buttons - Show when items selected */}
        {selectedIds.length > 0 && (
          <div className="flex gap-2 ml-auto">
            {isAdmin && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleBulkHideShow(true)}
                  data-testid="bulk-hide-btn"
                  className="gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md"
                >
                  <EyeOff className="w-4 h-4" />
                  Hide ({selectedIds.length})
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleBulkHideShow(false)}
                  data-testid="bulk-show-btn"
                  className="gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md"
                >
                  <Eye className="w-4 h-4" />
                  Show ({selectedIds.length})
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={handleEditSelected}
              data-testid="edit-selected-btn"
              className="gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md"
            >
              <Edit3 className="w-4 h-4" />
              Edit ({selectedIds.length})
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={triggerBulkDelete}
              data-testid="bulk-delete-btn"
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedIds.length})
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Info banner for error_manager */}
      {isErrorManager && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
          <AlertTriangle className="w-4 h-4" />
          <span>You can only edit product names (Primary, 2nd, 3rd, 4th names). Click on any name to edit.</span>
        </div>
      )}

      {/* Tabs for All Products / Branded Products */}
      <div className="flex items-center gap-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === "all" 
              ? "border-orange-500 text-orange-600" 
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          All Products ({products.length})
        </button>
        {/* Branded tab - hidden for error_manager */}
        {!isErrorManager && (
        <button
          onClick={() => setActiveTab("branded")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "branded" 
              ? "border-orange-500 text-orange-600" 
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Tag className="w-4 h-4" />
          Branded Products
          <span className="bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full text-xs font-semibold">
            {products.filter(p => p.branded_mrp && String(p.branded_mrp).trim() !== "" && String(p.branded_mrp).trim() !== "0").length}
          </span>
        </button>
        )}
      </div>

      {/* Show Branded Products List or Regular Table */}
      {activeTab === "branded" && !isErrorManager ? (
        <BrandedProductsList token={token} />
      ) : (
      <>
      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-white">
        <div className="overflow-x-auto sm:max-h-[calc(100vh-400px)] sm:overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full data-table">
            <thead className="sticky top-0 z-10">
              {/* Main Header Row with Sorting */}
              <tr className="bg-gradient-to-r from-slate-800 via-slate-800 to-slate-900">
                {/* Checkbox column - only for admin */}
                {!isErrorManager && (
                  <th className="px-3 py-3 w-10 bg-slate-800 border-b border-slate-700">
                    <Checkbox
                      checked={selectedIds.length === paginatedProducts.length && paginatedProducts.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                      className="border-slate-500"
                    />
                  </th>
                )}
                {effectiveColumns.map(col => (
                  <th 
                    key={col.key} 
                    style={{ minWidth: col.width }} 
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-100 bg-slate-800 border-b border-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{col.label}</span>
                      {col.sortable && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button 
                              className={`p-1 rounded hover:bg-slate-700 transition-colors flex-shrink-0 relative ${
                                getSortOrder(col.key) ? 'text-orange-400 bg-slate-700' : 'text-slate-400'
                              }`}
                              data-testid={`sort-btn-${col.key}`}
                            >
                              {getSortDirection(col.key) === "asc" ? (
                                col.sortType === "number" ? <ArrowUp01 className="w-3.5 h-3.5" /> : <ArrowUpAZ className="w-3.5 h-3.5" />
                              ) : getSortDirection(col.key) === "desc" ? (
                                col.sortType === "number" ? <ArrowDown10 className="w-3.5 h-3.5" /> : <ArrowDownAZ className="w-3.5 h-3.5" />
                              ) : (
                                col.sortType === "number" ? <ArrowUp01 className="w-3.5 h-3.5 opacity-50" /> : <ArrowUpAZ className="w-3.5 h-3.5 opacity-50" />
                              )}
                              {/* Sort order badge */}
                              {getSortOrder(col.key) && (
                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-orange-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                  {getSortOrder(col.key)}
                                </span>
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="text-xs">
                              Sort Options {getSortOrder(col.key) && <span className="text-orange-500">(#{getSortOrder(col.key)})</span>}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {col.sortType === "number" ? (
                              <>
                                <DropdownMenuCheckboxItem
                                  checked={getSortDirection(col.key) === "asc"}
                                  onCheckedChange={() => handleSort(col.key, "asc")}
                                >
                                  <ArrowUp01 className="w-4 h-4 mr-2" />
                                  Small → Big
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuCheckboxItem
                                  checked={getSortDirection(col.key) === "desc"}
                                  onCheckedChange={() => handleSort(col.key, "desc")}
                                >
                                  <ArrowDown10 className="w-4 h-4 mr-2" />
                                  Big → Small
                                </DropdownMenuCheckboxItem>
                              </>
                            ) : (
                              <>
                                <DropdownMenuCheckboxItem
                                  checked={getSortDirection(col.key) === "asc"}
                                  onCheckedChange={() => handleSort(col.key, "asc")}
                                >
                                  <ArrowUpAZ className="w-4 h-4 mr-2" />
                                  A → Z
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuCheckboxItem
                                  checked={getSortDirection(col.key) === "desc"}
                                  onCheckedChange={() => handleSort(col.key, "desc")}
                                >
                                  <ArrowDownAZ className="w-4 h-4 mr-2" />
                                  Z → A
                                </DropdownMenuCheckboxItem>
                              </>
                            )}
                            {getSortOrder(col.key) && (
                              <>
                                <DropdownMenuSeparator />
                                <button
                                  onClick={() => setSortConfigs(prev => prev.filter(s => s.key !== col.key))}
                                  className="w-full px-2 py-1.5 text-left text-sm text-red-500 hover:bg-red-50 rounded"
                                >
                                  Remove from Sort
                                </button>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </th>
                ))}
                {/* Actions column header for error_manager */}
                {isErrorManager && (
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-100 bg-slate-800 border-b border-slate-700" style={{ minWidth: '80px' }}>
                    Actions
                  </th>
                )}
              </tr>

              {/* Search Row - Fixed below header */}
              <tr className="bg-slate-100 border-b-2 border-slate-300">
                {/* Checkbox column search - only for admin */}
                {!isErrorManager && (
                  <th className="px-3 py-2 bg-slate-100">
                    <Search className="w-4 h-4 text-slate-400 mx-auto" />
                  </th>
                )}
                {effectiveColumns.map(col => (
                  <th key={col.key} className="px-2 py-2 bg-slate-100">
                    {SEARCHABLE_COLUMNS.includes(col.key) ? (
                      <div className="relative">
                        <Input
                          placeholder={`Search...`}
                          value={columnSearches[col.key] || ""}
                          onChange={(e) => handleColumnSearch(col.key, e.target.value)}
                          className="h-7 text-xs bg-white border-slate-300 pr-7"
                          data-testid={`search-${col.key}`}
                        />
                        {columnSearches[col.key] && (
                          <button
                            onClick={() => handleColumnSearch(col.key, "")}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </th>
                ))}
                {/* Actions column search placeholder for error_manager */}
                {isErrorManager && (
                  <th className="px-2 py-2 bg-slate-100">
                    <span className="text-slate-300 text-xs">—</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center py-12 text-muted-foreground">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : paginatedProducts.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <FileSpreadsheet className="w-16 h-16 text-slate-300" />
                      <p className="text-slate-500 font-medium">No products found</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowUploadDialog(true)}
                        className="gap-2 mt-2 border-orange-300 hover:bg-orange-50"
                      >
                        <Upload className="w-4 h-4 text-orange-500" />
                        Upload Price List
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedProducts.map((product, index) => {
                  // Check if product has any alias names to show in tooltip
                  const hasAliases = product.second_name || product.third_name || product.fourth_name;
                  const aliasTooltip = hasAliases ? (
                    <div className="text-xs space-y-1">
                      {product.second_name && (
                        <div><span className="font-semibold text-blue-300">2nd:</span> <span className="uppercase">{product.second_name}</span></div>
                      )}
                      {product.third_name && (
                        <div><span className="font-semibold text-green-300">3rd:</span> <span className="uppercase">{product.third_name}</span></div>
                      )}
                      {product.fourth_name && (
                        <div><span className="font-semibold text-yellow-300">4th:</span> <span className="uppercase">{product.fourth_name}</span></div>
                      )}
                    </div>
                  ) : null;
                  
                  return (
                  <TooltipProvider key={product.id} delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <tr
                          className={`transition-colors ${hasBrandedMRP(product) && !isErrorManager ? "branded-row" : ""} ${hasAliases ? "cursor-help" : ""}`}
                          style={hasBrandedMRP(product) && !isErrorManager ? { backgroundColor: '#fff0f3' } : {}}
                        >
                          {/* Checkbox column - only for admin */}
                          {!isErrorManager && (
                            <td className="px-3 py-2 border-b border-slate-100">
                              <Checkbox
                                checked={selectedIds.includes(product.id)}
                                onCheckedChange={(checked) => handleSelectOne(product.id, checked)}
                                data-testid={`select-${product.id}`}
                              />
                            </td>
                          )}
                          {effectiveColumns.map(col => (
                            <td
                              key={col.key}
                              className={`px-3 py-2.5 text-sm border-b border-slate-100 ${
                                col.key === "branded_mrp" || col.key === "serial_no" || col.key === "price" ? "font-mono font-medium" : ""
                              } ${col.key !== "serial_no" ? "cursor-pointer hover:bg-slate-100/50" : ""}`}
                              onClick={() => handleCellClick(product, col.key)}
                              style={{ fontSize: '0.8125rem' }}
                            >
                              {editingCell?.productId === product.id && editingCell?.column === col.key ? (
                                <Input
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={handleKeyDown}
                                  onBlur={handleCellSave}
                                  className="h-7 text-sm"
                                  autoFocus
                                  data-testid={`edit-${col.key}`}
                                />
                              ) : col.key === "serial_no" ? (
                                <span className="text-slate-500">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</span>
                              ) : col.key === "is_hidden" ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleHidden(product.id, product.is_hidden);
                                  }}
                                  disabled={!isAdmin}
                                  className={`p-1 rounded transition-colors ${
                                    product.is_hidden 
                                      ? "bg-red-100 text-red-600 hover:bg-red-200" 
                                      : "bg-green-100 text-green-600 hover:bg-green-200"
                                  } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                  title={isAdmin ? (product.is_hidden ? "Click to Show" : "Click to Hide") : "Only admins can change visibility"}
                                >
                                  {product.is_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              ) : col.key === "image_link" && product[col.key] ? (
                                <a
                                  href={product[col.key]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline truncate block max-w-[100px]"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View
                                </a>
                              ) : col.key === "price" && hasBrandedMRP(product) ? (
                                <div className="flex items-center gap-1">
                                  <span className="uppercase">{formatPrice(product[col.key])}</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowPriceHistory(product);
                                    }}
                                    className="p-0.5 rounded hover:bg-orange-200 text-orange-500"
                                    title="View Price History"
                                  >
                                    <History className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (col.key === "price" || col.key === "branded_mrp" || col.key === "suggested_usd_price") ? (
                                <span className="uppercase block truncate" title={formatPrice(product[col.key])}>
                                  {formatPrice(product[col.key])}
                                </span>
                              ) : (
                                <span className="uppercase block truncate" title={product[col.key] || ""}>
                                  {product[col.key] || "-"}
                                </span>
                              )}
                            </td>
                          ))}
                          {/* Actions column for error_manager */}
                          {isErrorManager && (
                            <td className="px-3 py-2 border-b border-slate-100">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditName(product);
                                }}
                                className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 border-blue-200"
                                title="Edit Product Names"
                                data-testid={`edit-name-${product.id}`}
                              >
                                <Edit3 className="w-3.5 h-3.5 mr-1" />
                                Edit
                              </Button>
                            </td>
                          )}
                        </tr>
                      </TooltipTrigger>
                      {hasAliases && (
                        <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 max-w-xs">
                          <p className="font-semibold mb-1 text-orange-400">Alias Names:</p>
                          {aliasTooltip}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination Banner - Matching Header Style */}
        {totalCount > 0 && (
          <div className="bg-gradient-to-r from-slate-800 via-slate-800 to-slate-900 px-4 py-3 flex items-center justify-between border-t border-slate-700">
            <div className="text-sm text-slate-300">
              Showing <span className="font-semibold text-white">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> to{" "}
              <span className="font-semibold text-white">{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}</span> of{" "}
              <span className="font-semibold text-white">{totalCount}</span> products
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="h-8 px-2 bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 hover:text-white disabled:opacity-50"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-8 px-2 bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className={`h-8 w-8 p-0 ${
                        currentPage === pageNum 
                          ? 'bg-orange-500 text-white hover:bg-orange-600 border-orange-500' 
                          : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 hover:text-white'
                      }`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-8 px-2 bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 hover:text-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="h-8 px-2 bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 hover:text-white disabled:opacity-50"
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* Price History Modal */}
      {showPriceHistory && (
        <BrandedPriceHistory
          product={showPriceHistory}
          token={token}
          onClose={() => setShowPriceHistory(null)}
          onPriceUpdate={fetchProducts}
        />
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-500" />
              Upload Product Price List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="drop-zone cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 mx-auto text-orange-500 mb-4" />
              <p className="text-foreground font-medium">Click to upload or drag and drop</p>
              <p className="text-sm text-muted-foreground mt-1">Excel files only (.xlsx, .xls)</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div className="flex justify-between items-center pt-2 border-t">
              <Button variant="link" onClick={handleDownloadTemplate} className="text-sm p-0 text-emerald-600">
                Download sample template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Mode Selection Dialog */}
      <Dialog open={showUploadModeDialog} onOpenChange={(open) => {
        setShowUploadModeDialog(open);
        if (!open) {
          setPendingUploadFile(null);
          setDryRunPreview(null);
          setDryRunLoading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      }}>
        <DialogContent className={dryRunPreview ? "max-w-xl max-h-[90vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-500" />
              {dryRunPreview
                ? (dryRunPreview.mode === "append" ? "Confirm Append" : "Confirm Wipe & Replace")
                : "Choose Upload Mode"}
            </DialogTitle>
          </DialogHeader>

          {/* -------- View 1: choose mode -------- */}
          {!dryRunPreview && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                File: <span className="font-medium text-foreground">{pendingUploadFile?.name}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                How would you like to handle existing products?
              </p>

              <div className="grid grid-cols-1 gap-3">
                {/* Append Mode — also runs a dry-run first */}
                <button
                  data-testid="upload-mode-append-btn"
                  onClick={() => handleFileUpload("append")}
                  disabled={dryRunLoading}
                  className="flex items-start gap-3 p-4 rounded-lg border-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-400 transition-colors text-left disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0">
                    {dryRunLoading ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Plus className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">Append Products</p>
                    <p className="text-xs text-green-600 mt-1">
                      Add new products AND update prices on same-name matches.
                      Preview the delta first — nothing is written until you confirm.
                    </p>
                  </div>
                </button>

                {/* Wipe Mode — triggers dry-run first */}
                <button
                  data-testid="upload-mode-wipe-btn"
                  onClick={() => handleFileUpload("wipe")}
                  disabled={dryRunLoading}
                  className="flex items-start gap-3 p-4 rounded-lg border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-colors text-left disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center flex-shrink-0">
                    {dryRunLoading ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">Wipe & Replace</p>
                    <p className="text-xs text-red-600 mt-1">
                      Preview the parse first, then confirm. Existing products stay intact until you confirm.
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* -------- View 2: dry-run preview panel -------- */}
          {dryRunPreview && (
            <div className="space-y-4 py-4" data-testid="dry-run-preview-panel">
              <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4">
                <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">
                  Parse Result
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">File</span>
                    <p className="font-medium text-slate-900 truncate" title={dryRunPreview.filename}>
                      {dryRunPreview.filename}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Headers detected on</span>
                    <p className="font-medium text-slate-900">Row {dryRunPreview.header_row}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">{dryRunPreview.mode === "append" ? "New products" : "Rows to insert"}</span>
                    <p className="font-semibold text-green-700" data-testid="dry-run-to-insert">
                      {dryRunPreview.to_insert}
                    </p>
                  </div>
                  {dryRunPreview.mode === "append" ? (
                    <div>
                      <span className="text-slate-500">Price changes</span>
                      <p className="font-semibold text-blue-700" data-testid="dry-run-to-update">
                        {dryRunPreview.to_update || 0}
                      </p>
                    </div>
                  ) : null}
                  <div>
                    <span className="text-slate-500">
                      {dryRunPreview.mode === "append" ? "Unchanged (skipped)" : "Duplicates skipped"}
                    </span>
                    <p className="font-semibold text-amber-700">
                      {dryRunPreview.skipped_duplicates}
                    </p>
                  </div>
                  {dryRunPreview.mode !== "append" && (
                    <div className="col-span-2">
                      <span className="text-slate-500">Existing products that will be deleted</span>
                      <p className="font-semibold text-red-700" data-testid="dry-run-will-delete">
                        {dryRunPreview.will_delete}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Price-change delta table (append only) */}
              {dryRunPreview.mode === "append" && dryRunPreview.update_samples?.length > 0 && (
                <div
                  className="rounded-lg border border-blue-200 bg-blue-50/40 p-3"
                  data-testid="dry-run-update-samples"
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-xs uppercase tracking-wider text-blue-700 font-semibold">
                      Top price changes (first {dryRunPreview.update_samples.length})
                      {dryRunPreview.to_update > dryRunPreview.update_samples.length && (
                        <span className="ml-1 text-slate-500 normal-case font-normal">
                          of {dryRunPreview.to_update}
                        </span>
                      )}
                    </p>
                    {dryRunPreview.all_updates?.length > 0 && (
                      <button
                        type="button"
                        data-testid="dry-run-download-delta-btn"
                        onClick={downloadDeltaReport}
                        className="text-[10px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold uppercase tracking-wider flex items-center gap-1"
                        title="Download full price-delta report as Excel"
                      >
                        <Download className="w-3 h-3" />
                        Delta report ({dryRunPreview.all_updates.length})
                      </button>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 text-left">
                        <th className="pb-1 font-medium">Product</th>
                        <th className="pb-1 font-medium text-right">Old</th>
                        <th className="pb-1 font-medium w-6"></th>
                        <th className="pb-1 font-medium text-right">New</th>
                        <th className="pb-1 font-medium text-right">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRunPreview.update_samples.map((s, i) => {
                        const oldNum = parseFloat(s.old_price) || 0;
                        const newNum = parseFloat(s.new_price) || 0;
                        const delta = newNum - oldNum;
                        const pct = oldNum ? ((delta / oldNum) * 100) : 0;
                        const isUp = delta > 0;
                        return (
                          <tr key={i} className="border-t border-blue-100">
                            <td className="py-1 pr-2 truncate max-w-[220px] text-slate-800" title={s.product_name}>
                              {s.product_name}
                            </td>
                            <td className="py-1 text-right text-slate-500 tabular-nums">{s.old_price || "—"}</td>
                            <td className="py-1 text-center text-slate-400">→</td>
                            <td className="py-1 text-right font-semibold text-slate-900 tabular-nums">{s.new_price || "—"}</td>
                            <td className={`py-1 text-right tabular-nums font-medium ${isUp ? 'text-red-600' : 'text-emerald-600'}`}>
                              {isUp ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {dryRunPreview.sample_products?.length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Sample of what will be inserted
                  </p>
                  <ul className="text-xs text-slate-700 space-y-1">
                    {dryRunPreview.sample_products.map((n, i) => (
                      <li key={i} className="truncate">• {n}</li>
                    ))}
                  </ul>
                </div>
              )}

              {dryRunPreview.column_map?.length > 0 && (() => {
                const unmapped = dryRunPreview.column_map.filter(c => c.kind === 'unmapped');
                return (
                  <div
                    className="rounded-lg border border-slate-200 p-3"
                    data-testid="dry-run-column-map"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                        Column Mapping
                      </p>
                      {unmapped.length > 0 && (
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold"
                          data-testid="dry-run-unmapped-count"
                        >
                          {unmapped.length} unmapped
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {dryRunPreview.column_map.map((c, i) => {
                        const cls = c.kind === 'mapped'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : c.kind === 'ignored'
                            ? 'bg-slate-50 text-slate-500 border-slate-200'
                            : 'bg-amber-50 text-amber-800 border-amber-300';
                        const icon = c.kind === 'mapped' ? '✔' : c.kind === 'ignored' ? '–' : '⚠';
                        const title = c.kind === 'unmapped'
                          ? (c.suggestion
                              ? `"${c.source}" is not recognized. Did you mean "${c.suggestion}"?`
                              : `"${c.source}" is not a recognized product field — its data will be ignored on insert.`)
                          : `"${c.source}" → ${c.target}`;
                        return (
                          <span
                            key={i}
                            title={title}
                            className={`text-[11px] px-2 py-1 rounded border font-medium inline-flex items-center gap-1 ${cls}`}
                          >
                            <span>{icon} {c.source}</span>
                            {c.kind === 'mapped' && (
                              <span className="text-slate-400 font-normal">→ {c.target}</span>
                            )}
                            {c.kind === 'unmapped' && c.suggestion && (
                              <>
                                <span
                                  data-testid={`dry-run-suggestion-${c.source}`}
                                  className="text-amber-600 font-normal italic"
                                >
                                  did you mean &ldquo;{c.suggestion}&rdquo;?
                                </span>
                                <button
                                  type="button"
                                  data-testid={`dry-run-teach-alias-${c.source}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveColumnAliasAndRetry(c.source, c.suggestion);
                                  }}
                                  disabled={dryRunLoading}
                                  className="ml-1 px-1.5 py-0 rounded bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-[10px] font-semibold uppercase tracking-wider"
                                  title={`Remember "${c.source}" as "${c.suggestion}" and re-parse the file`}
                                >
                                  Use as {c.suggestion}
                                </button>
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>
                    {unmapped.length > 0 && (
                      <p className="text-[11px] text-amber-700 mt-2">
                        ⚠ Data in unmapped columns will be dropped. Check for header typos before confirming.
                      </p>
                    )}
                  </div>
                );
              })()}

              {dryRunPreview.mode !== "append" && (
                <p className="text-xs text-red-600 bg-red-50 rounded p-2 border border-red-200">
                  ⚠️ Confirming will delete all {dryRunPreview.will_delete} existing products and cannot be undone.
                </p>
              )}
              {dryRunPreview.mode === "append" && (dryRunPreview.to_update || 0) > 0 && (
                <p className="text-xs text-blue-700 bg-blue-50 rounded p-2 border border-blue-200">
                  ℹ️ {dryRunPreview.to_update} existing product price{dryRunPreview.to_update === 1 ? '' : 's'} will be updated. A price-history entry is recorded for each change.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            {dryRunPreview ? (
              <>
                <Button
                  variant="outline"
                  data-testid="dry-run-back-btn"
                  onClick={() => setDryRunPreview(null)}
                >
                  Back
                </Button>
                {dryRunPreview.mode === "append" ? (
                  <Button
                    data-testid="dry-run-confirm-btn"
                    onClick={() => handleFileUpload("append")}
                    className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="w-4 h-4" />
                    Confirm & Append
                    ({dryRunPreview.to_insert} new
                    {dryRunPreview.to_update ? `, ${dryRunPreview.to_update} updates` : ''})
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    data-testid="dry-run-confirm-btn"
                    onClick={() => handleFileUpload("wipe")}
                    className="gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Confirm & Wipe ({dryRunPreview.to_insert} rows)
                  </Button>
                )}
              </>
            ) : (
              <Button variant="outline" onClick={() => {
                setShowUploadModeDialog(false);
                setPendingUploadFile(null);
                setDryRunPreview(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}>
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          setBulkEditMode(false);
          setBulkEditData({});
          setEditingProduct(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-500" />
              {bulkEditMode ? `Bulk Edit (${selectedIds.length} products)` : "Edit Product"}
            </DialogTitle>
          </DialogHeader>
          
          {bulkEditMode ? (
            // Bulk Edit Mode
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Fill in the fields you want to update. Empty fields will not be changed.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {ALL_COLUMNS.filter(c => c.key !== "serial_no").map(col => {
                  // Bulk edit: hidden toggle (tri-state — leave unchanged / force hidden / force visible)
                  if (col.key === "is_hidden") {
                    const current = bulkEditData.is_hidden;
                    return (
                      <div key={col.key} className="space-y-1.5">
                        <Label className="text-sm font-medium">{col.label}</Label>
                        <select
                          value={current === true ? "true" : current === false ? "false" : ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setBulkEditData(prev => {
                              const next = { ...prev };
                              if (v === "") delete next.is_hidden;
                              else next.is_hidden = v === "true";
                              return next;
                            });
                          }}
                          className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                        >
                          <option value="">Leave unchanged</option>
                          <option value="false">Visible in list</option>
                          <option value="true">Hidden from list</option>
                        </select>
                      </div>
                    );
                  }
                  // Use AutoSuggestInput for specific fields
                  if (["generic_name", "company", "category"].includes(col.key)) {
                    return (
                      <div key={col.key} className="space-y-1.5">
                        <Label className="text-sm font-medium">{col.label}</Label>
                        <AutoSuggestInput
                          value={bulkEditData[col.key] || ""}
                          onChange={(val) => setBulkEditData(prev => ({
                            ...prev,
                            [col.key]: val
                          }))}
                          field={col.key}
                          token={token}
                          placeholder={`Update ${col.label.toLowerCase()}...`}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={col.key} className="space-y-1.5">
                      <Label className="text-sm font-medium">{col.label}</Label>
                      <Input
                        value={bulkEditData[col.key] || ""}
                        onChange={(e) => setBulkEditData(prev => ({
                          ...prev,
                          [col.key]: e.target.value
                        }))}
                        placeholder={`Update ${col.label.toLowerCase()}...`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : editingProduct && (
            // Single Edit Mode
            <div className="grid grid-cols-2 gap-4">
              {ALL_COLUMNS.filter(c => c.key !== "serial_no").map(col => {
                // Boolean field — render as checkbox
                if (col.key === "is_hidden") {
                  return (
                    <div key={col.key} className="space-y-1.5">
                      <Label className="text-sm font-medium">{col.label}</Label>
                      <label className="flex items-center gap-2 h-10 px-3 border border-slate-200 rounded-md bg-white cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!editingProduct.is_hidden}
                          onChange={(e) => setEditingProduct(prev => ({
                            ...prev,
                            is_hidden: e.target.checked,
                          }))}
                          className="w-4 h-4"
                          data-testid="edit-product-is-hidden"
                        />
                        <span className="text-sm text-slate-700">
                          {editingProduct.is_hidden ? "Hidden from list" : "Visible in list"}
                        </span>
                      </label>
                    </div>
                  );
                }
                // Use AutoSuggestInput for specific fields
                if (["generic_name", "company", "category"].includes(col.key)) {
                  return (
                    <div key={col.key} className="space-y-1.5">
                      <Label className="text-sm font-medium">{col.label}</Label>
                      <AutoSuggestInput
                        value={editingProduct[col.key] || ""}
                        onChange={(val) => setEditingProduct(prev => ({
                          ...prev,
                          [col.key]: val
                        }))}
                        field={col.key}
                        token={token}
                        placeholder={`Enter ${col.label.toLowerCase()}`}
                      />
                    </div>
                  );
                }
                return (
                  <div key={col.key} className="space-y-1.5">
                    <Label className="text-sm font-medium">{col.label}</Label>
                    <Input
                      value={editingProduct[col.key] || ""}
                      onChange={(e) => setEditingProduct(prev => ({
                        ...prev,
                        [col.key]: e.target.value
                      }))}
                      placeholder={`Enter ${col.label.toLowerCase()}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={bulkEditMode ? handleBulkSave : handleSaveProduct}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
            >
              {bulkEditMode ? `Update ${selectedIds.length} Products` : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Name Dialog (for error_manager) */}
      <Dialog open={showEditNameDialog} onOpenChange={(open) => {
        setShowEditNameDialog(open);
        if (!open) {
          setEditNameProduct(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-500" />
              Edit Product Names
            </DialogTitle>
            <DialogDescription>
              Update the product name and its aliases below. All names will be used for product matching.
            </DialogDescription>
          </DialogHeader>
          
          {editNameProduct && (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">1</span>
                    Primary Name
                  </Label>
                  <Input
                    value={editNameProduct.product_name || ""}
                    onChange={(e) => setEditNameProduct(prev => ({
                      ...prev,
                      product_name: e.target.value
                    }))}
                    placeholder="Enter primary product name"
                    className="uppercase"
                    data-testid="edit-name-primary"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-green-100 text-green-700 text-xs font-semibold flex items-center justify-center">2</span>
                    Second Name (Alias)
                  </Label>
                  <Input
                    value={editNameProduct.second_name || ""}
                    onChange={(e) => setEditNameProduct(prev => ({
                      ...prev,
                      second_name: e.target.value
                    }))}
                    placeholder="Enter second name / alias"
                    className="uppercase"
                    data-testid="edit-name-second"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-yellow-100 text-yellow-700 text-xs font-semibold flex items-center justify-center">3</span>
                    Third Name (Alias)
                  </Label>
                  <Input
                    value={editNameProduct.third_name || ""}
                    onChange={(e) => setEditNameProduct(prev => ({
                      ...prev,
                      third_name: e.target.value
                    }))}
                    placeholder="Enter third name / alias"
                    className="uppercase"
                    data-testid="edit-name-third"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center">4</span>
                    Fourth Name (Alias)
                  </Label>
                  <Input
                    value={editNameProduct.fourth_name || ""}
                    onChange={(e) => setEditNameProduct(prev => ({
                      ...prev,
                      fourth_name: e.target.value
                    }))}
                    placeholder="Enter fourth name / alias"
                    className="uppercase"
                    data-testid="edit-name-fourth"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-cyan-100 text-cyan-700 text-xs font-semibold flex items-center justify-center">5</span>
                    Fifth Name (Alias)
                  </Label>
                  <Input
                    value={editNameProduct.fifth_name || ""}
                    onChange={(e) => setEditNameProduct(prev => ({ ...prev, fifth_name: e.target.value }))}
                    placeholder="Enter fifth name / alias"
                    className="uppercase"
                    data-testid="edit-name-fifth"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-rose-100 text-rose-700 text-xs font-semibold flex items-center justify-center">6</span>
                    Sixth Name (Alias)
                  </Label>
                  <Input
                    value={editNameProduct.sixth_name || ""}
                    onChange={(e) => setEditNameProduct(prev => ({ ...prev, sixth_name: e.target.value }))}
                    placeholder="Enter sixth name / alias"
                    className="uppercase"
                    data-testid="edit-name-sixth"
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditNameDialog(false)} disabled={savingName}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveProductName}
              disabled={savingName || !editNameProduct?.product_name?.trim()}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
              data-testid="save-name-btn"
            >
              {savingName ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print/Export Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={(open) => {
        setShowPrintDialog(open);
        if (!open) { setPrintAllProducts(null); }
      }}>
        <DialogContent mobileFullScreen className="max-w-md flex flex-col max-h-[95vh] sm:max-h-[90vh] p-0 sm:p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
            <DialogTitle className="font-heading flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              Print / Export Products
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            {/* Column Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Columns to Include</label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_COLUMNS.filter(c => c.key !== "serial_no").map(col => (
                  <label key={col.key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={printColumns.includes(col.key)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setPrintColumns(prev => [...prev, col.key]);
                        } else {
                          setPrintColumns(prev => prev.filter(k => k !== col.key));
                        }
                      }}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Filter Options */}
            <div>
              <label className="text-sm font-medium mb-2 block">Filter By</label>
              <div className="flex gap-2">
                <select
                  value={printFilter.type}
                  onChange={(e) => setPrintFilter({ type: e.target.value, value: [] })}
                  className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">All Products</option>
                  <option value="generic_name">Generic Name</option>
                  <option value="company">Company</option>
                  <option value="category">Category</option>
                  <option value="branded_mrp">Branded Only</option>
                </select>
                {printFilter.type && printFilter.type !== "branded_mrp" && (
                  <Popover open={filterValueOpen} onOpenChange={setFilterValueOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={filterValueOpen}
                        className="flex-1 h-9 justify-between"
                        data-testid="print-filter-value-trigger"
                      >
                        <span className="truncate">
                          {(printFilter.value || []).length === 0
                            ? "Select value(s)…"
                            : (printFilter.value || []).length === 1
                              ? printFilter.value[0]
                              : `${printFilter.value.length} selected`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search… (toggle multi-select)" />
                        <CommandList>
                          <CommandEmpty>No results found.</CommandEmpty>
                          {(printFilter.value || []).length > 0 && (
                            <div className="flex items-center justify-between px-2 py-1.5 border-b">
                              <span className="text-[11px] text-slate-500">
                                {printFilter.value.length} selected
                              </span>
                              <button
                                type="button"
                                className="text-[11px] text-red-600 hover:underline"
                                onClick={() => setPrintFilter(prev => ({ ...prev, value: [] }))}
                              >
                                Clear all
                              </button>
                            </div>
                          )}
                          <CommandGroup className="max-h-60 overflow-auto">
                            {(autocompleteOptions[printFilter.type] || []).map((option) => {
                              const checked = (printFilter.value || []).includes(option);
                              return (
                                <CommandItem
                                  key={option}
                                  value={option}
                                  onSelect={() => {
                                    setPrintFilter((prev) => {
                                      const arr = Array.isArray(prev.value) ? [...prev.value] : [];
                                      const idx = arr.indexOf(option);
                                      if (idx >= 0) arr.splice(idx, 1);
                                      else arr.push(option);
                                      return { ...prev, value: arr };
                                    });
                                    // Keep popover open so multiple can be selected
                                  }}
                                  data-testid={`print-filter-option-${option}`}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${checked ? "opacity-100" : "opacity-0"}`}
                                  />
                                  {option}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              {/* Show selected chips below the row for clarity */}
              {Array.isArray(printFilter.value) && printFilter.value.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {printFilter.value.map((v) => (
                    <span
                      key={v}
                      className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px] px-2 py-0.5 rounded-full"
                    >
                      {v}
                      <button
                        type="button"
                        onClick={() =>
                          setPrintFilter((prev) => ({
                            ...prev,
                            value: prev.value.filter((x) => x !== v),
                          }))
                        }
                        className="hover:text-indigo-900"
                        aria-label={`Remove ${v}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Preview count */}
            <div className="p-3 bg-slate-50 rounded-lg border">
              {printAllLoading ? (
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading full product list…
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    <strong>{printPreviewProducts.length}</strong> products will be included
                    {printFilter.type === "branded_mrp" && " (Branded Only)"}
                  </p>
                  {printAllProducts && (
                    <p className="text-[11px] text-emerald-600 mt-1">
                      Covering all {printAllProducts.length.toLocaleString()} products in DB
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 px-4 pb-4 pt-3 sm:px-6 sm:pb-6 sm:pt-3 border-t bg-background flex-shrink-0">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)} className="w-full sm:w-auto" data-testid="print-export-cancel-btn">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowPrintDialog(false);
                setShowPrintPreview(true);
              }} 
              disabled={printAllLoading || printPreviewProducts.length === 0}
              className="gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white w-full sm:w-auto"
              data-testid="print-export-preview-btn"
            >
              <Printer className="w-4 h-4" />
              Preview & Print
            </Button>
            <Button onClick={handleExportPPTX} className="gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white w-full sm:w-auto" data-testid="print-export-catalog-btn">
              <Presentation className="w-4 h-4" />
              Catalog
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Product Dialog */}
      <Dialog open={showAddProductDialog} onOpenChange={setShowAddProductDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-lg">+</span>
              Add New Product
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Product Name - Required */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">
                  Product Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={newProduct.product_name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, product_name: e.target.value }))}
                  placeholder="Enter product name"
                  className="border-green-200 focus:border-green-400"
                  autoComplete="off"
                />
              </div>
              
              {/* Generic Name */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Generic Name</Label>
                <AutoSuggestInput
                  value={newProduct.generic_name}
                  onChange={(val) => setNewProduct(prev => ({ ...prev, generic_name: val }))}
                  field="generic_name"
                  token={token}
                  placeholder="Enter generic name (suggestions after 3 chars)"
                />
              </div>
              
              {/* Company */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Company</Label>
                <AutoSuggestInput
                  value={newProduct.company}
                  onChange={(val) => setNewProduct(prev => ({ ...prev, company: val }))}
                  field="company"
                  token={token}
                  placeholder="Enter company name (suggestions after 3 chars)"
                />
              </div>
              
              {/* Box Packaging */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Box Packaging</Label>
                <Input
                  value={newProduct.box_packaging}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, box_packaging: e.target.value }))}
                  placeholder="e.g., 10x10"
                  autoComplete="off"
                />
              </div>
              
              {/* Weight per Strip */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Weight (per strip/unit)</Label>
                <Input
                  value={newProduct.weight_per_strip}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, weight_per_strip: e.target.value }))}
                  placeholder="e.g., 50g"
                  autoComplete="off"
                />
              </div>
              
              {/* Unit */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Unit</Label>
                <Input
                  value={newProduct.unit}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, unit: e.target.value }))}
                  placeholder="e.g., TAB, CAP, ML"
                  autoComplete="off"
                />
              </div>
              
              {/* Category */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Category</Label>
                <AutoSuggestInput
                  value={newProduct.category}
                  onChange={(val) => setNewProduct(prev => ({ ...prev, category: val }))}
                  field="category"
                  token={token}
                  placeholder="e.g., Antibiotics (suggestions after 3 chars)"
                />
              </div>
              
              {/* Price */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Price (₹)</Label>
                <Input
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="Enter price"
                  autoComplete="off"
                />
              </div>
              
              {/* Branded MRP */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Branded MRP</Label>
                <Input
                  type="number"
                  value={newProduct.branded_mrp}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, branded_mrp: e.target.value }))}
                  placeholder="Enter branded MRP"
                  autoComplete="off"
                />
              </div>
              
              {/* Suggested USD Price */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Suggested $ Price</Label>
                <Input
                  type="number"
                  value={newProduct.suggested_usd_price}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, suggested_usd_price: e.target.value }))}
                  placeholder="Enter USD price"
                  autoComplete="off"
                />
              </div>
              
              {/* Second Name */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">2nd Name / Alias</Label>
                <Input
                  value={newProduct.second_name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, second_name: e.target.value }))}
                  placeholder="Enter alternate name"
                  autoComplete="off"
                />
              </div>
              
              {/* Third Name */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">3rd Name / Alias</Label>
                <Input
                  value={newProduct.third_name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, third_name: e.target.value }))}
                  placeholder="Enter 3rd alternate name"
                  autoComplete="off"
                />
              </div>
              
              {/* Fourth Name */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">4th Name / Alias</Label>
                <Input
                  value={newProduct.fourth_name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, fourth_name: e.target.value }))}
                  placeholder="Enter 4th alternate name"
                  autoComplete="off"
                />
              </div>
              
              {/* Narration */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Narration</Label>
                <Input
                  value={newProduct.narration}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, narration: e.target.value }))}
                  placeholder="Enter narration/notes"
                  autoComplete="off"
                />
              </div>
              
              {/* Image Link */}
              <div className="space-y-1.5 col-span-2">
                <Label className="text-sm font-medium">Image Link</Label>
                <Input
                  value={newProduct.image_link}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, image_link: e.target.value }))}
                  placeholder="Enter image URL"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddProductDialog(false);
                setNewProduct({
                  product_name: "",
                  generic_name: "",
                  company: "",
                  box_packaging: "",
                  weight_per_strip: "",
                  unit: "",
                  category: "",
                  price: "",
                  branded_mrp: "",
                  suggested_usd_price: "",
                  second_name: "",
                  third_name: "",
                  fourth_name: "",
                  narration: "",
                  image_link: ""
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddProduct}
              disabled={addingProduct || !newProduct.product_name.trim()}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
            >
              {addingProduct ? "Adding..." : "Add Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Preview Window */}
      <PrintPreview
        isOpen={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        products={printPreviewProducts}
        columns={printColumns}
        filterBy={printFilter.type}
        filterValue={printFilter.value}
        token={token}
      />

      {/* Price History Upload Dialog */}
      <Dialog open={showPriceHistoryUpload} onOpenChange={setShowPriceHistoryUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              Branded Products Price History
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Download Template Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-slate-700">Step 1: Download Template</h4>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadPriceHistoryTemplate(false)}
                  className="flex flex-col items-center gap-1 h-auto py-3 border-blue-200 hover:bg-blue-50"
                >
                  <FileSpreadsheet className="w-5 h-5 text-blue-500" />
                  <span className="text-xs">Blank Template</span>
                  <span className="text-xs text-slate-500">Headers only</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadPriceHistoryTemplate(true)}
                  className="flex flex-col items-center gap-1 h-auto py-3 border-green-200 hover:bg-green-50"
                >
                  <FileText className="w-5 h-5 text-green-500" />
                  <span className="text-xs">With Branded Products</span>
                  <span className="text-xs text-slate-500">Only branded names</span>
                </Button>
              </div>
            </div>
            
            {/* Upload Section */}
            <div className="space-y-3 border-t pt-4">
              <h4 className="font-medium text-sm text-slate-700">Step 2: Upload Filled Template</h4>
              <div className="bg-slate-50 rounded-lg p-4 border border-dashed border-slate-300">
                <input
                  type="file"
                  ref={priceHistoryFileInputRef}
                  onChange={handlePriceHistoryUpload}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <Button
                    variant="outline"
                    onClick={() => priceHistoryFileInputRef.current?.click()}
                    disabled={priceHistoryUploading}
                    className="gap-2"
                  >
                    {priceHistoryUploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Select Excel File
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Accepts .xlsx, .xls files
                  </p>
                </div>
              </div>
            </div>
            
            {/* Expected Format */}
            <div className="text-xs text-slate-600 bg-amber-50 rounded-lg p-3 border border-amber-200">
              <p className="font-medium text-amber-700 mb-1">Expected Columns:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>product_name</strong> (required)</li>
                <li><strong>price</strong> (required)</li>
                <li><strong>effective_from</strong> (required)</li>
                <li>effective_to (optional)</li>
                <li>mrp (optional)</li>
                <li>notes (optional)</li>
              </ul>
              <p className="font-medium text-amber-700 mt-2 mb-1">Supported Date Formats:</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-0.5 bg-white rounded border">DD-MM-YYYY</span>
                <span className="px-2 py-0.5 bg-white rounded border">DD/MM/YYYY</span>
                <span className="px-2 py-0.5 bg-white rounded border">DDMMYYYY</span>
                <span className="px-2 py-0.5 bg-white rounded border">DD.MM.YYYY</span>
                <span className="px-2 py-0.5 bg-white rounded border">YYYY-MM-DD</span>
              </div>
            </div>
            
            {/* Row-wise Format Instructions */}
            <div className="text-xs text-slate-600 bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="font-medium text-blue-700 mb-1">📋 ROW-WISE Format:</p>
              <p className="mb-2">For multiple price periods of the same product, add <strong>multiple rows</strong>:</p>
              <div className="bg-white/60 rounded p-2 font-mono text-[10px] space-y-0.5">
                <div>Crocin | 100 | 120 | 01-Jan-2025 | 05-Jan-2025</div>
                <div>Crocin | 110 | 130 | 06-Jan-2025 | 15-Jan-2025</div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceHistoryUpload(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Upload Errors Panel */}
      <Dialog open={showPriceHistoryErrors} onOpenChange={setShowPriceHistoryErrors}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Price History Upload Errors
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Upload Summary */}
            {priceHistoryUploadStats && (
              <div className="flex gap-3 text-sm">
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg">
                  <Check className="w-4 h-4" />
                  <span><strong>{priceHistoryUploadStats.added}</strong> Added</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg">
                  <RefreshCw className="w-4 h-4" />
                  <span><strong>{priceHistoryUploadStats.updated}</strong> Updated</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  <span><strong>{priceHistoryErrors.length}</strong> Errors</span>
                </div>
              </div>
            )}
            
            {/* Error Categories Summary */}
            {priceHistoryErrors.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 border">
                <p className="text-sm font-medium text-slate-700 mb-2">Error Summary:</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  {(() => {
                    const notFound = priceHistoryErrors.filter(e => e.includes("not found")).length;
                    const invalidPrice = priceHistoryErrors.filter(e => e.includes("Invalid price")).length;
                    const invalidDate = priceHistoryErrors.filter(e => e.includes("Invalid effective")).length;
                    const other = priceHistoryErrors.length - notFound - invalidPrice - invalidDate;
                    return (
                      <>
                        {notFound > 0 && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                            <Package className="w-3 h-3 inline mr-1" />
                            Product Not Found: {notFound}
                          </span>
                        )}
                        {invalidPrice > 0 && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                            <IndianRupee className="w-3 h-3 inline mr-1" />
                            Invalid Price: {invalidPrice}
                          </span>
                        )}
                        {invalidDate > 0 && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Invalid Date: {invalidDate}
                          </span>
                        )}
                        {other > 0 && (
                          <span className="px-2 py-1 bg-slate-200 text-slate-700 rounded">
                            Other: {other}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            {/* Error List */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 flex items-center justify-between">
                <span>Error Details ({priceHistoryErrors.length} rows)</span>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {priceHistoryErrors.map((error, idx) => {
                  // Parse error type for styling
                  const isNotFound = error.includes("not found");
                  const isInvalidPrice = error.includes("Invalid price");
                  const isInvalidDate = error.includes("Invalid effective");
                  
                  return (
                    <div 
                      key={idx} 
                      className={`px-3 py-2 text-sm border-b last:border-b-0 flex items-start gap-2
                        ${isNotFound ? 'bg-red-50' : isInvalidPrice ? 'bg-orange-50' : isInvalidDate ? 'bg-purple-50' : 'bg-white'}`}
                    >
                      <span className="flex-shrink-0 mt-0.5">
                        {isNotFound && <Package className="w-4 h-4 text-red-500" />}
                        {isInvalidPrice && <IndianRupee className="w-4 h-4 text-orange-500" />}
                        {isInvalidDate && <Calendar className="w-4 h-4 text-purple-500" />}
                        {!isNotFound && !isInvalidPrice && !isInvalidDate && <AlertCircle className="w-4 h-4 text-slate-500" />}
                      </span>
                      <span className="text-slate-700">{error}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* How to Fix Section */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm font-medium text-blue-700 mb-2">💡 How to Fix:</p>
              <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                <li><strong>Product Not Found:</strong> Check product name spelling or add the product first in Products tab</li>
                <li><strong>Invalid Price:</strong> Ensure price is a number (no text, symbols except ₹ $ ,)</li>
                <li><strong>Invalid Date:</strong> Use format: YYYY-MM-DD, DD-MM-YYYY, or DD/MM/YYYY</li>
              </ul>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => {
                  // Copy errors to clipboard
                  const errorText = priceHistoryErrors.join('\n');
                  navigator.clipboard.writeText(errorText);
                  toast.success("Errors copied to clipboard");
                }}
              >
                <FileText className="w-4 h-4" />
                Copy Errors
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => {
                  // Get product names that were not found
                  const notFoundProducts = priceHistoryErrors
                    .filter(e => e.includes("not found"))
                    .map(e => {
                      const match = e.match(/Product '(.+)' not found/);
                      return match ? match[1] : null;
                    })
                    .filter(Boolean);
                  
                  if (notFoundProducts.length > 0) {
                    navigator.clipboard.writeText(notFoundProducts.join('\n'));
                    toast.success(`${notFoundProducts.length} product names copied`);
                  } else {
                    toast.info("No 'not found' products to copy");
                  }
                }}
              >
                <Package className="w-4 h-4" />
                Copy Missing Products
              </Button>
            </div>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowPriceHistoryErrors(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setShowPriceHistoryErrors(false);
                setShowPriceHistoryUpload(true);
              }}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={deleteAction?.type === "bulk" ? "Delete Products" : "Delete Product"}
        description={deleteAction?.type === "bulk" 
          ? `You are about to delete ${deleteAction?.count} products. This action cannot be undone.`
          : "Are you sure you want to delete this product?"}
        onConfirm={executeBulkDelete}
        loading={deletingItems}
      />
    </div>
  );
};

export default ProductsPage;
